import React, { useState, useEffect, useMemo } from 'react';
import PlotlyChart from '../../components/charts/PlotlyChart';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { hfService } from '../../services/hfService';
import { parseDfJson } from '../../utils/formatters';
import { HEATMAP_DIVERGING } from '../../components/charts/chartTheme';

const PERIOD_OPTIONS = [
  { value: 'full', label: 'Full Period' },
  { value: '12', label: '12 Months' },
  { value: '36', label: '36 Months' },
  { value: '60', label: '60 Months' },
];

const CorrelationPage = ({ analyticsParams, cachedFetch }) => {
  const [period, setPeriod] = useState('full');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!analyticsParams?.dataset_id) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchFn = () => hfService.getCorrelation({ ...analyticsParams, period });
        const res = cachedFetch ? await cachedFetch('correlation-' + period, fetchFn) : await fetchFn();
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [analyticsParams, period]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Build heatmap trace
  const heatmapTrace = useMemo(() => {
    if (!data?.matrix) return null;
    const { index: rowLabels, columns: colLabels, data: matrix } = parseDfJson(data.matrix);

    if (!rowLabels.length || !colLabels.length) return null;

    // On mobile, abbreviate fund names to first 3 characters
    const displayRowLabels = isMobile ? rowLabels.map(l => l.substring(0, 3)) : rowLabels;
    const displayColLabels = isMobile ? colLabels.map(l => l.substring(0, 3)) : colLabels;

    // Build annotation text
    const annotationText = matrix.map(row =>
      row.map(v => (v != null ? v.toFixed(2) : ''))
    );

    return {
      trace: {
        z: matrix,
        x: displayColLabels,
        y: displayRowLabels,
        type: 'heatmap',
        colorscale: HEATMAP_DIVERGING,
        zmin: -1,
        zmax: 1,
        zmid: 0,
        text: annotationText,
        texttemplate: isMobile ? '' : '%{text}',
        textfont: { size: 9 },
        hovertemplate: '%{y} vs %{x}: %{z:.2f}<extra></extra>',
        showscale: true,
        colorbar: {
          title: 'Correlation',
          titleside: 'right',
          len: 0.8,
        },
      },
      annotations: buildAnnotations(matrix, displayRowLabels, displayColLabels),
      size: rowLabels.length,
    };
  }, [data, isMobile]);

  if (loading) return <LoadingSpinner message="Loading correlation data..." />;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-2">Failed to load correlation data</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
      </div>
    );
  }

  const selectCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none w-full';

  return (
    <div>
      {/* Period Selector */}
      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="w-full max-w-xs">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Period</label>
          <select
            className={selectCls}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {data?.funds && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing correlations for {data.funds.length} fund{data.funds.length !== 1 ? 's' : ''}
            {period !== 'full' ? ` (last ${period} months)` : ' (full period)'}
          </p>
        )}
      </div>

      {/* Correlation Heatmap */}
      {heatmapTrace && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Return Correlation Matrix</h3>
          <div className="overflow-x-auto">
            <PlotlyChart
              data={[heatmapTrace.trace]}
              layout={{
                xaxis: {
                  side: 'bottom',
                  tickangle: -45,
                  automargin: true,
                },
                yaxis: {
                  autorange: 'reversed',
                  automargin: true,
                },
                margin: isMobile ? { l: 50, r: 30, t: 20, b: 60 } : { l: 120, r: 60, t: 30, b: 120 },
                annotations: isMobile ? [] : heatmapTrace.annotations,
              }}
              style={{
                minHeight: isMobile
                  ? Math.max(300, heatmapTrace.size * 30 + 80)
                  : Math.max(400, heatmapTrace.size * 40 + 150),
              }}
              interactive
            />
          </div>
        </div>
      )}

      {!heatmapTrace && !loading && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p>No correlation data available for the selected parameters.</p>
        </div>
      )}
    </div>
  );
};

/**
 * Build Plotly annotation objects for each cell in the correlation matrix.
 * Each annotation displays the numeric value centered in its cell, with
 * text color adjusted for readability against the heatmap background.
 */
function buildAnnotations(matrix, rowLabels, colLabels) {
  const annotations = [];
  for (let i = 0; i < rowLabels.length; i++) {
    for (let j = 0; j < colLabels.length; j++) {
      const val = matrix[i][j];
      if (val == null) continue;
      annotations.push({
        x: colLabels[j],
        y: rowLabels[i],
        text: val.toFixed(2),
        font: {
          size: 10,
          color: Math.abs(val) > 0.65 ? '#fff' : '#333',
        },
        showarrow: false,
        xref: 'x',
        yref: 'y',
      });
    }
  }
  return annotations;
}

export default CorrelationPage;
